<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  KINDS,
  KIND_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  createHub,
  deleteHub,
  hubInput,
  listHubs,
  updateHub,
  type Hub,
  type HubKind,
  type HubStatus,
} from '../api/hubs'

const rows = ref<Hub[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', kind: '', page: 1, size: 20 })

const formOpen = ref(false)
const editing = ref<Hub | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive({
  code: '',
  name: '',
  kind: 'depot' as HubKind,
  city: '',
  address: '',
  status: 'active' as HubStatus,
})

const codeError = computed(() => (form.code.trim() ? '' : '请输入场站编码'))

const formErrorText = computed(() => codeError.value)

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listHubs({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
      kind: query.kind || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    code: '',
    name: '',
    kind: 'depot' as HubKind,
    city: '',
    address: '',
    status: 'active' as HubStatus,
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

function openEdit(row: Hub): void {
  editing.value = row
  Object.assign(form, {
    code: row.code,
    name: row.name,
    kind: row.kind,
    city: row.city,
    address: row.address,
    status: row.status,
  })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input = hubInput({ ...form })
  try {
    if (editing.value) await updateHub(editing.value.id, input)
    else await createHub(input)
    formOpen.value = false
    await load(editing.value ? '场站已更新' : '场站已创建')
  } catch (e) {
    // 编码重复是 409，这里原样把 trace_id 露出来
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: Hub): Promise<void> {
  notice.value = ''
  error.value = null
  try {
    await deleteHub(row.id)
    await load(`已删除场站「${row.code}」`)
  } catch (e) {
    // 已被计划区段当起讫点引用时后端 409
    error.value = e
  }
}
</script>

<template>
  <div class="yc-hub">
    <header class="yc-hub__bar">
      <el-input
        v-model="query.keyword"
        class="yc-hub__keyword"
        placeholder="编码 / 名称 / 城市"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.kind" class="yc-hub__ref" placeholder="全部类型" clearable @change="search">
        <el-option v-for="k in KINDS" :key="k" :label="KIND_LABELS[k]" :value="k" />
      </el-select>
      <el-select v-model="query.status" class="yc-hub__ref" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'mtm:hub:create'" type="primary" @click="openCreate">新建场站</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-hub__notice">{{ notice }}</span>
    </header>

    <div v-if="error" class="yc-hub__error">
      <TraceIdAlert :error="error" />
    </div>

    <el-card v-loading="loading" class="yc-hub__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="code" label="编码" min-width="120" />
        <el-table-column label="名称" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">{{ row.name || '—' }}</template>
        </el-table-column>
        <el-table-column label="类型" width="110">
          <template #default="{ row }">
            <el-tag size="small" type="info">{{ KIND_LABELS[row.kind as HubKind] ?? row.kind }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="城市" min-width="100" show-overflow-tooltip>
          <template #default="{ row }">{{ row.city || '—' }}</template>
        </el-table-column>
        <el-table-column label="地址" min-width="180" show-overflow-tooltip>
          <template #default="{ row }">{{ row.address || '—' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as HubStatus] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as HubStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template #default="{ row }">
            <el-button v-perm="'mtm:hub:update'" link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-popconfirm
              title="确认删除该场站？已被计划区段当起讫点引用的场站不能删除。"
              width="300"
              @confirm="remove(row)"
            >
              <template #reference>
                <el-button v-perm="'mtm:hub:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有场站"
        description="计划区段的起讫点都指向场站，先建仓库、铁路站场、港口或机场货站。"
      >
        <el-button v-perm="'mtm:hub:create'" type="primary" @click="openCreate">新建场站</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-hub__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑场站 · ${editing.code}` : '新建场站'"
      width="520px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-hub__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="编码" :error="codeError">
          <el-input v-model="form.code" maxlength="32" placeholder="如 SH01" />
        </el-form-item>

        <el-form-item label="名称">
          <el-input v-model="form.name" maxlength="64" placeholder="如 上海外高桥堆场，可留空" />
        </el-form-item>

        <el-form-item label="类型">
          <el-select v-model="form.kind" class="yc-hub__full">
            <el-option v-for="k in KINDS" :key="k" :label="KIND_LABELS[k]" :value="k" />
          </el-select>
        </el-form-item>

        <el-form-item label="城市">
          <el-input v-model="form.city" maxlength="64" placeholder="如 上海，可留空" />
        </el-form-item>

        <el-form-item label="地址">
          <el-input v-model="form.address" maxlength="255" placeholder="可留空" />
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio value="active">启用</el-radio>
            <el-radio value="disabled">停用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
        <span v-if="formErrorText" class="yc-hub__hint yc-hub__hint--inline">{{ formErrorText }}</span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-hub {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 200px;
  }

  &__ref {
    width: 150px;
  }

  &__full {
    width: 100%;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__hint {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.7;

    &--inline {
      margin-left: var(--yc-space-3);
    }
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }
}
</style>
